import axios from "axios";
import { environment } from "../../environment";

export interface HelpItemSalesforce {
  Id?: string;
  Title__c: string;
  Description__c?: string;
  Type__c: 'PDF' | 'Video';
  URL__c?: string;
  Order__c?: number; // Salesforce Order field for drag-and-drop ordering
  CreatedDate?: string;
  LastModifiedDate?: string;
}

export interface HelpApiResponse {
  success: boolean;
  data?: HelpItemSalesforce | HelpItemSalesforce[];
  message?: string;
  error?: string;
}

export const HelpService = () => {
  const baseUrl = environment.contentUrl + "help";

  const getAll = async (): Promise<HelpItemSalesforce[]> => {
    try {
      const res = await axios.get(baseUrl);
      if (res?.data?.success && res?.data?.data) {
        return res.data.data;
      } else {
        throw new Error(res?.data?.error || "Failed to fetch help items");
      }
    } catch (err) {
      console.error("Error fetching help items:", err);
      throw err;
    }
  };

  const getById = async (id: string): Promise<HelpItemSalesforce> => {
    try {
      const res = await axios.get(`${baseUrl}/${id}`);
      if (res?.data?.success && res?.data?.data) {
        return res.data.data;
      } else {
        throw new Error(res?.data?.error || "Failed to fetch help item");
      }
    } catch (err) {
      console.error(`Error fetching help item ${id}:`, err);
      throw err;
    }
  };

  const create = async (title: string, description: string | undefined, type: 'PDF' | 'Video', url?: string, file?: File, order?: number): Promise<HelpItemSalesforce> => {
    try {
      const formData = new FormData();
      formData.append('title', title);
      
      if (description) {
        formData.append('description', description);
      }
      
      formData.append('type', type);
      
      if (url) {
        formData.append('url', url);
      }
      
      if (order !== undefined) {
        formData.append('order', order.toString());
      }
      
      if (file) {
        formData.append('file', file);
      }

      const res = await axios.post(baseUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      
      if (res?.data?.success && res?.data?.data) {
        return res.data.data;
      } else {
        throw new Error(res?.data?.error || "Failed to create help item");
      }
    } catch (err) {
      console.error("Error creating help item:", err);
      throw err;
    }
  };

  const update = async (id: string, title?: string, description?: string, type?: 'PDF' | 'Video', url?: string, file?: File, order?: number): Promise<HelpItemSalesforce> => {
    try {
      const formData = new FormData();
      
      if (title) {
        formData.append('title', title);
      }
      
      if (description !== undefined) {
        formData.append('description', description);
      }
      
      if (type) {
        formData.append('type', type);
      }
      
      if (url) {
        formData.append('url', url);
      }
      
      if (order !== undefined) {
        formData.append('order', order.toString());
      }
      
      if (file) {
        formData.append('file', file);
      }

      const res = await axios.put(`${baseUrl}/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      
      if (res?.data?.success && res?.data?.data) {
        return res.data.data;
      } else {
        throw new Error(res?.data?.error || "Failed to update help item");
      }
    } catch (err) {
      console.error(`Error updating help item ${id}:`, err);
      throw err;
    }
  };

  const deleteItem = async (id: string): Promise<void> => {
    try {
      const res = await axios.delete(`${baseUrl}/${id}`);
      if (!res?.data?.success) {
        throw new Error(res?.data?.error || "Failed to delete help item");
      }
    } catch (err) {
      console.error(`Error deleting help item ${id}:`, err);
      throw err;
    }
  };

  const bulkUpdateOrder = async (items: Array<{ id: string; order: number }>): Promise<void> => {
    try {
      const res = await axios.put(`${baseUrl}/order/bulk`, {
        items
      });
      
      if (!res?.data?.success) {
        throw new Error(res?.data?.error || "Failed to update help items order");
      }
    } catch (err) {
      console.error("Error updating help items order:", err);
      throw err;
    }
  };

  return {
    getAll,
    getById,
    create,
    update,
    delete: deleteItem,
    bulkUpdateOrder,
  };
};